import React, { useState } from "react";
import { Calendar } from "lucide-react";

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const QUARTERS = [
    { id: 1, label: "Q1 (Jan–Mar)" },
    { id: 2, label: "Q2 (Apr–Jun)" },
    { id: 3, label: "Q3 (Jul–Sep)" },
    { id: 4, label: "Q4 (Oct–Dec)" },
];

const pad2 = (n) => String(n).padStart(2, "0");
const isoDate = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

const lastDayOfMonth = (year, monthIndex0) => new Date(year, monthIndex0 + 1, 0).getDate();

const monthRange = (year, monthIndex0) => ({
    start: isoDate(year, monthIndex0, 1),
    end: isoDate(year, monthIndex0, lastDayOfMonth(year, monthIndex0)),
});

const quarterRange = (year, quarter) => {
    const startMonth = (quarter - 1) * 3;
    const endMonth = startMonth + 2;
    return {
        start: isoDate(year, startMonth, 1),
        end: isoDate(year, endMonth, lastDayOfMonth(year, endMonth)),
    };
};

const currentYear = new Date().getFullYear();
const YEARS = [currentYear, currentYear - 1, currentYear - 2];

// Same output contract as DateRangePicker's rangeToParams: { range, start, end }
// or { range: key } — here we always emit a concrete start/end, tagged with
// key: "custom" for the backend, plus extra mode/year/month/quarter fields
// (ignored by rangeToParams, used here only to restore the UI on reload).
const MonthQuarterRangePicker = ({ range, setRange }) => {
    // Which tab is being SHOWN — independent of the committed range's mode,
    // since clicking "Custom" needs to reveal the date inputs immediately,
    // before any range has actually been applied yet.
    const [activeTab, setActiveTab] = useState(range.mode || "month");
    const mode = activeTab;
    const [customOpen, setCustomOpen] = useState(false);
    const [customStart, setCustomStart] = useState(range.mode === "custom" ? range.start : "");
    const [customEnd, setCustomEnd] = useState(range.mode === "custom" ? range.end : "");

    const year = range.year || currentYear;
    const month = range.month ?? new Date().getMonth();
    const quarter = range.quarter || Math.floor(new Date().getMonth() / 3) + 1;

    const selectMonth = (y, m) => {
        const { start, end } = monthRange(y, m);
        setRange({ key: "custom", mode: "month", year: y, month: m, start, end });
    };
    const selectQuarter = (y, q) => {
        const { start, end } = quarterRange(y, q);
        setRange({ key: "custom", mode: "quarter", year: y, quarter: q, start, end });
    };
    const applyCustom = () => {
        if (!customStart || !customEnd) return;
        setRange({ key: "custom", mode: "custom", start: customStart, end: customEnd });
        setCustomOpen(false);
    };

    return (
        <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                <button onClick={() => { setActiveTab("month"); selectMonth(year, month); }}
                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition ${mode === "month" ? "bg-white shadow text-teal-700" : "text-gray-500"}`}>
                    Month
                </button>
                <button onClick={() => { setActiveTab("quarter"); selectQuarter(year, quarter); }}
                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition ${mode === "quarter" ? "bg-white shadow text-teal-700" : "text-gray-500"}`}>
                    Quarter
                </button>
                <button onClick={() => { setActiveTab("custom"); setCustomOpen(true); }}
                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition ${mode === "custom" ? "bg-white shadow text-teal-700" : "text-gray-500"}`}>
                    Custom
                </button>
            </div>

            {mode === "month" && (
                <div className="flex items-center gap-2">
                    <select value={month} onChange={(e) => selectMonth(year, Number(e.target.value))}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500">
                        {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                    </select>
                    <select value={year} onChange={(e) => selectMonth(Number(e.target.value), month)}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500">
                        {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            )}

            {mode === "quarter" && (
                <div className="flex items-center gap-2">
                    <select value={quarter} onChange={(e) => selectQuarter(year, Number(e.target.value))}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500">
                        {QUARTERS.map((q) => <option key={q.id} value={q.id}>{q.label}</option>)}
                    </select>
                    <select value={year} onChange={(e) => selectQuarter(Number(e.target.value), quarter)}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500">
                        {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            )}

            {mode === "custom" && (
                <div className="relative">
                    <button onClick={() => setCustomOpen((o) => !o)}
                        className="px-3 py-1.5 text-xs font-bold rounded-lg border-2 border-teal-500 bg-teal-50 text-teal-700 transition flex items-center gap-1.5">
                        <Calendar size={12} /> {range.start} → {range.end}
                    </button>
                    {customOpen && (
                        <div className="absolute z-20 top-full left-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg p-3 flex items-center gap-2">
                            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <span className="text-gray-400 text-xs">to</span>
                            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <button onClick={applyCustom} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-bold hover:bg-teal-700">
                                Apply
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MonthQuarterRangePicker;